<template>
  <section class="mx-3 lg:mx-0 max-w-screen-2xl">
    <div class="mt-10">
      <div class="flex flex-col gap-10">
        <div class="flex items-center justify-between">
          <div class="flex gap-3 items-center">
            <p
              class="text-base text-root-2 font-extrabold md:text-lg"
              id="settingsTourItem2"
            >
              IP Address List
            </p>
            <div class="rounded-md bg-border-10 py-1.5 px-3">
              <p class="font-normal md:text-base text-sm">
                {{ ipAddresses.length }} of 10
              </p>
            </div>
          </div>
          <div
            @click="addIPAddress"
            class="px-10 cursor-pointer bg-primary rounded-lg text-white lg:text-sm text-xs py-4 font-normal flex items-center justify-center hover-animation-subtle"
          >
            Add IP Address
          </div>
        </div>
        <div
          class="table-wrapper w-full overflow-x-auto"
          v-if="isLoading"
          test-id="isLoading"
        >
          <table-loader />
        </div>
        <div class="table-wrapper w-full overflow-x-auto" v-else>
          <div class="lg:px-8 px-3 pb-5">
            <div v-if="isLoading" class="mt-14">
              <spinner />
            </div>
            <el-table
              v-else
              class="w-full relative font-normal text-root-2 text-xs lg:text-sm overflow-x-scroll"
              style="width: 100%"
              :data="ipAddresses"
              max-height="500"
            >
              <el-table-column>
                <template slot="header">
                  <div
                    class="flex items-center justify-between py-2 px-2 lg:px-5 font-semibold"
                  >
                    <p
                      style="word-break: normal"
                      class="text-root-2 text-xs lg:text-sm"
                    >
                      IP Address
                    </p>
                  </div>
                </template>
                <template slot-scope="scope">
                  <div class="">
                    <p
                      class="text-root-2 text-xs lg:text-sm font-normal px-2 lg:px-5 overflow-x-scroll"
                      style="word-break: normal"
                    >
                      {{ scope.row.ipAddress }}
                    </p>
                  </div>
                </template>
              </el-table-column>
              <el-table-column width="60">
                <template slot-scope="scope">
                  <el-tooltip
                    class="item"
                    effect="light"
                    :disabled="canManageIPAddresses"
                    :content="'No access to delete IP Address'"
                    placement="bottom"
                  >
                    <div
                      v-on="
                        !canManageIPAddresses
                          ? {}
                          : {
                              click: () => deleteIPAddress(scope.row.ipAddress)
                            }
                      "
                      class="text-primary px-3 py-2.5 flex text-center justify-center !w-full"
                      :class="[
                        {
                          't-opacity-50 cursor-not-allowed':
                            !canManageIPAddresses
                        }
                      ]"
                    >
                      <span :class="{ 'cursor-pointer': canManageIPAddresses }">
                        <delete-icon />
                      </span>
                    </div>
                  </el-tooltip>
                </template>
              </el-table-column>
            </el-table>

            <transaction-empty-state
              use="receive-payment"
              v-if="noIpAddresses && !isLoading"
            >
              <h3 class="font-semibold text-root-2">No IP Addresses</h3>
              <p
                class="font-light text-root-4 text-xs flex items-center justify-center mt-2"
              >
                <span class="w-3/4 text-center"
                  >Your IP Addresses show up here.</span
                >
              </p>
            </transaction-empty-state>
          </div>
        </div>
      </div>
    </div>
    <verify-auth-dialog
      v-if="showVerifyAuthDialog"
      :authState="authState"
      :showDialog="showVerifyAuthDialog"
      @closeDialog="closeDialog('showVerifyAuthDialog')"
      @deleteWhitelistedIP="deleteWhitelistedIP"
    />
    <attention-dialog
      v-if="showDeleteIPDialog"
      :showDialog="showDeleteIPDialog"
      @closeDialog="closeDialog('showDeleteIPDialog')"
      @deleteWhitelistedIP="verifyAuth('deleteWhitelistedIP')"
      :mode="mode"
      :message="message"
    />
  </section>
</template>
<script>
import statusClass2 from '@/mixins/app.js'
import breakpoints from '@/utils/breakpoints.js'
import getApiCredentials from '@/mixins/externalApi.js'
import userKinds from '@/mixins/userKinds.js'

export default {
  name: 'IPAddressList',
  emits: ['create'],
  components: {
    TransactionEmptyState: () =>
      import('@/components/empty-states/TransactionEmptyState.vue'),
    DeleteIcon: () => import('@/assets/icons/DeleteIcon.vue'),
    VerifyAuthDialog: () => import('@/components/v2/auth/VerifyAuthDialog'),
    AttentionDialog: () => import('@/components/v2/app/AttentionDialog.vue'),
    TableLoader: () => import('@/components/v2/skeletalLoaders/TableLoader.vue')
  },
  mixins: [statusClass2, getApiCredentials, userKinds],
  data () {
    return {
      isLoading: false,
      breakpoints,
      showVerifyAuthDialog: false,
      authState: '',
      selectedIp: '',
      showDeleteIPDialog: false,
      mode: 'deleteWhitelistedIP',
      message: 'Are you sure you want to delete this IP address?'
    }
  },
  methods: {
    addIPAddress () {
      this.$emit('create')
    },
    deleteWhitelistedIP (twoFaCode) {
      const payload = {
        ipAddress: this.selectedIp,
        twoFaCode: twoFaCode.code
      }
      this.$store
        .dispatch('externalApi/deleteWhitelistedIP', { ...payload })
        .then(async () => {
          this.closeDialog('showVerifyAuthDialog')
          await this.getApiCredentials()
          this.$notify({
            title: 'Success',
            message: 'Whitelisted IP deleted successfully',
            type: 'success'
          })
        })
        .catch(() => {
          this.closeDialog('showVerifyAuthDialog')
        })
        .finally(() => {
          this.closeDialog('showVerifyAuthDialog')
        })
    },
    verifyAuth (authState) {
      this.closeDialog('showDeleteIPDialog')
      this.openDialog('showVerifyAuthDialog')
      this.authState = authState
    },
    deleteIPAddress (ip) {
      this.selectedIp = ip
      this.openDialog('showDeleteIPDialog')
    },
    closeDialog (arg) {
      this[arg] = false
    },
    openDialog (arg) {
      this[arg] = true
    }
  },
  computed: {
    ipAddresses () {
      if (!this.$store.state.externalApi?.apiCredentials?.ipAddress) return []
      return this.$store.state.externalApi.apiCredentials.ipAddress.map(
        (ip) => ({
          ipAddress: ip,
          createdAt: new Date().toISOString() // Using new date for now. Till created at is added to the DB
        })
      )
    },
    noIpAddresses () {
      return this.ipAddresses.length < 1
    }
  }
}
</script>

<style scoped>
>>> .el-tooltip__popper.is-dark,
>>> .el-tooltip__popper.is-light,
>>> .el-tooltip__popper {
  background: white !important;
}

>>> .el-table {
  padding: 0;
  background: transparent;
  border-collapse: separate;
}

>>> .el-table table {
  border-spacing: 0 16px;
}
.scrollable-container {
  max-height: 60vh;
  overflow-y: auto;
}

>>> .el-table th {
  border: none !important;
  background: #f7f7f7;
  padding: 0;
}

>>> .el-table tr {
  background: white;
  border-radius: 8px;
}
>>> .el-table td {
  border-bottom: none !important;
  background: white;
}

>>> .el-table td:first-child {
  border-top-left-radius: 8px;
  border-bottom-left-radius: 8px;
}
>>> .el-table td:last-child {
  border-top-right-radius: 8px;
  border-bottom-right-radius: 8px;
}

>>> .el-table::before {
  height: 0px;
}
>>> .el-table--enable-row-hover .el-table__body tr:hover > td.el-table__cell {
  background: white !important;
}
>>> .el-pagination.is-background .btn-next.disabled,
.el-pagination.is-background .btn-next:disabled,
.el-pagination.is-background .btn-prev.disabled,
.el-pagination.is-background .btn-prev:disabled,
.el-pagination.is-background .el-pager li.disabled {
  background: green !important;
}
>>> .el-pagination.is-background .btn-next,
.el-pagination.is-background .btn-prev,
.el-pagination.is-background .el-pager li {
  background: transparent !important;
  border: 0.941331px solid rgba(207, 219, 213, 0.7);
  border-radius: 7.53065px;
}

>>> .el-pagination.is-background .btn-prev,
.el-pagination.is-background .btn-next,
.el-pagination.is-background .el-pager li {
  background: transparent !important;
  border: 0.941331px solid rgba(207, 219, 213, 0.7);
  border-radius: 7.53065px;
}

>>> li.number.active {
  background-color: var(--color-primary) !important;
  border: none !important;
  color: white !important;
  border-radius: 7.53065px !important;
}

>>> li.number {
  border: none !important;
  font-weight: 500;
  font-size: 12px;
  color: var(--text-2);
  background: transparent !important;
  /* color: var(--color-primary) !important ; */
}

>>> li.number:hover {
  border: 0.941331px solid var(--color-primary) !important;
  border-radius: 7.53065px !important;
  color: var(--text-2) !important;
}

>>> .el-input--prefix .el-input__inner {
  border: 1px solid var(--border-1);
  border-radius: 8px;
  background: var(--background-10);
}

>>> .el-table__empty-text {
  display: none;
  height: 0;
}

>>> .el-table__empty-block {
  height: 0 !important;
  min-height: 0;
}

>>> .el-tooltip__popper.is-light {
  background: white !important;
  border-color: white !important;
}
</style>
